/**
 * The one interpreter that executes every `RulesSpec`.
 *
 * Boundary contract:
 *  - **zero runtime imports.** The only import is `import type` from
 *    `schema.ts`, so the correctness core carries no dependencies at all;
 *  - no I/O, no `Date`, no `Math.random`, no logging, no ids, no player names;
 *  - it knows seats, never users;
 *  - every input is treated as frozen and is never mutated;
 *  - every exported function is total: it returns a value for every input,
 *    including nonsense ones, and never throws for in-type arguments. The sole
 *    sanctioned throw is `decodeBoard`'s `RangeError` on malformed text.
 *
 * `rules` is always the first argument and is never stored in the state, so a
 * `State` stays a plain, hand-buildable value.
 *
 * Specs are untrusted. `schema.ts`'s refinements are invisible to the type
 * system, so every loop here is bounded by the board's own size rather than by
 * anything a spec supplies. An in-type but nonsensical spec produces nonsense
 * output, never a hang and never a throw.
 */
import type { RulesSpec } from './schema.ts';

/** The seat numbers a spec declares, in rotation order. */
export type SeatOf<S extends RulesSpec> = S['seats'][number];
/** A board cell: empty, or owned by a seat. */
export type CellOf<S extends RulesSpec> = 0 | SeatOf<S>;
/** `index = row * cols + col`, length `cols * rows`. */
export type BoardOf<S extends RulesSpec> = readonly CellOf<S>[];
/** The literal win length a spec declares, when it is known statically. */
export type WinLengthOf<S extends RulesSpec> = S['terminal']['win'][number]['length'];

type LineBuilder<N extends number, Acc extends number[]> = Acc['length'] extends N
  ? Readonly<Acc>
  : LineBuilder<N, [...Acc, number]>;

/**
 * A winning line as a readonly tuple of exactly `N` board indices. Falls back to
 * a plain readonly array when the length is not a literal, which keeps the type
 * total for specs parsed at runtime.
 */
export type Line<N extends number> = number extends N ? readonly number[] : LineBuilder<N, []>;

export type Status<S extends RulesSpec> =
  | { readonly kind: 'in_play' }
  | { readonly kind: 'win'; readonly winner: SeatOf<S>; readonly line: Line<WinLengthOf<S>> }
  | { readonly kind: 'draw' };

export interface State<S extends RulesSpec> {
  readonly board: BoardOf<S>;
  readonly toMove: SeatOf<S>;
  readonly moveCount: number;
  readonly status: Status<S>;
}

/** A move is a single numeric parameter. Its meaning is fixed by `rules.moves`. */
export interface Move {
  readonly param: number;
}

/**
 * Checked in exactly this order. Only the codes a `drop` game can reach exist:
 * an unreachable variant would be an uncoverable branch.
 */
export type EngineError = 'game_over' | 'not_your_turn' | 'param_out_of_domain' | 'no_landing_cell';

export type ApplyResult<S extends RulesSpec> =
  | {
      readonly ok: true;
      readonly state: State<S>;
      /** The cell this move created. */
      readonly primary: number;
      readonly written: readonly number[];
    }
  | { readonly ok: false; readonly error: EngineError };

export type ReplayResult<S extends RulesSpec> =
  | { readonly ok: true; readonly state: State<S> }
  | { readonly ok: false; readonly atIndex: number; readonly error: EngineError };

const IN_PLAY = Object.freeze({ kind: 'in_play' } as const);
const DRAW = Object.freeze({ kind: 'draw' } as const);
const NO_MOVES: readonly Move[] = Object.freeze([]);

/* ------------------------------------------------------------------------- *
 * Geometry
 * ------------------------------------------------------------------------- */

export function boardSize(rules: RulesSpec): number {
  return rules.board.cols * rules.board.rows;
}

export function indexOf(rules: RulesSpec, row: number, col: number): number {
  return row * rules.board.cols + col;
}

export function rowOf(rules: RulesSpec, index: number): number {
  return Math.floor(index / rules.board.cols);
}

export function colOf(rules: RulesSpec, index: number): number {
  return index % rules.board.cols;
}

function inBounds(rules: RulesSpec, row: number, col: number): boolean {
  const { cols, rows } = rules.board;
  return row >= 0 && row < rows && col >= 0 && col < cols;
}

/**
 * `turnOrder: 'cyclic'` — the seat after `seat` in declared order. An unknown
 * seat yields `seats[0]`, because `indexOf` returns -1 and -1 + 1 is 0.
 *
 * The cast is safe by construction: the modulus keeps the index inside the
 * tuple, whose first two elements are statically present.
 */
export function nextSeat<S extends RulesSpec>(rules: S, seat: SeatOf<S>): SeatOf<S> {
  const seats = rules.seats;
  const position = seats.indexOf(seat);
  return seats[(position + 1) % seats.length] as SeatOf<S>;
}

/* ------------------------------------------------------------------------- *
 * Landing
 * ------------------------------------------------------------------------- */

/**
 * `param: 'column'` — the domain of a move parameter is `[0, cols)`, implied by
 * the board rather than restated in the move definition.
 */
function inDomain(rules: RulesSpec, param: number): boolean {
  return Number.isInteger(param) && param >= 0 && param < rules.board.cols;
}

/**
 * The cell a drop would occupy, or -1 when the column has no room.
 *
 * `legalMoves` and `applyMove` both go through this one function, so legality
 * ("landing exists") and execution can never disagree.
 */
function findLanding<S extends RulesSpec>(rules: S, board: BoardOf<S>, param: number): number {
  const { rows } = rules.board;
  const ascending = rules.moves[0].fill === 'ascending';
  for (let step = 0; step < rows; step += 1) {
    const row = ascending ? step : rows - 1 - step;
    const index = indexOf(rules, row, param);
    if (board[index] === rules.cells.empty) return index;
  }
  return -1;
}

/* ------------------------------------------------------------------------- *
 * Terminal conditions
 * ------------------------------------------------------------------------- */

/**
 * The winning line through `index`, or null.
 *
 * `through: 'last_placement'` is licensed by `cells.mutation: 'write_once'`
 * (enforced in `schema.ts`): when cells are never rewritten, only the cell just
 * played can complete a line, so scanning it alone is equivalent to a full scan.
 *
 * Line selection is specified, not incidental: walk backwards to the start of
 * the contiguous run, then forwards; if the run is long enough, take the window
 * at `offset = min(positionOfPlayedCell, run - length)`. That is the window
 * closest to the start of the run that still contains the played cell.
 *
 * Both walks are capped at the board size so a degenerate `[0, 0]` direction —
 * rejected by the schema, but reachable through a hand-built spec — terminates.
 */
function winLineThrough<S extends RulesSpec>(
  rules: S,
  board: BoardOf<S>,
  index: number,
  seat: SeatOf<S>,
): readonly number[] | null {
  const row = rowOf(rules, index);
  const col = colOf(rules, index);
  const cap = boardSize(rules);

  for (const condition of rules.terminal.win) {
    const length = condition.length;
    for (const [dRow, dCol] of condition.directions) {
      let startRow = row;
      let startCol = col;
      let back = 0;
      while (
        back < cap &&
        inBounds(rules, startRow - dRow, startCol - dCol) &&
        board[indexOf(rules, startRow - dRow, startCol - dCol)] === seat
      ) {
        startRow -= dRow;
        startCol -= dCol;
        back += 1;
      }

      const cells: number[] = [];
      let r = startRow;
      let c = startCol;
      while (cells.length < cap && inBounds(rules, r, c) && board[indexOf(rules, r, c)] === seat) {
        cells.push(indexOf(rules, r, c));
        r += dRow;
        c += dCol;
      }

      if (cells.length >= length) {
        const played = cells.indexOf(index);
        const offset = Math.min(played, cells.length - length);
        return cells.slice(offset, offset + length);
      }
    }
  }

  return null;
}

function boardFull<S extends RulesSpec>(rules: S, board: BoardOf<S>): boolean {
  const size = boardSize(rules);
  for (let index = 0; index < size; index += 1) {
    if (board[index] === rules.cells.empty) return false;
  }
  return true;
}

/**
 * `board_full` is the only draw condition in the vocabulary, so any declared
 * condition means "draw once no empty cell remains", and an empty `draw` list
 * means the game simply cannot be drawn.
 */
function isDrawn<S extends RulesSpec>(rules: S, board: BoardOf<S>): boolean {
  return rules.terminal.draw.length > 0 && boardFull(rules, board);
}

/* ------------------------------------------------------------------------- *
 * The public engine
 * ------------------------------------------------------------------------- */

export function createInitial<S extends RulesSpec>(rules: S, firstSeat: SeatOf<S>): State<S> {
  const board = new Array<CellOf<S>>(boardSize(rules)).fill(rules.cells.empty);
  return Object.freeze({
    board: Object.freeze(board),
    toMove: firstSeat,
    moveCount: 0,
    status: IN_PLAY,
  });
}

/** Moves a spec permits from this state, by ascending parameter. Empty when terminal. */
export function legalMoves<S extends RulesSpec>(rules: S, state: State<S>): readonly Move[] {
  if (state.status.kind !== 'in_play') return NO_MOVES;
  const moves: Move[] = [];
  for (let param = 0; param < rules.board.cols; param += 1) {
    if (findLanding(rules, state.board, param) !== -1) moves.push(Object.freeze({ param }));
  }
  return Object.freeze(moves);
}

export function applyMove<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  seat: SeatOf<S>,
  move: Move,
): ApplyResult<S> {
  if (state.status.kind !== 'in_play') return { ok: false, error: 'game_over' };
  if (seat !== state.toMove) return { ok: false, error: 'not_your_turn' };
  if (!inDomain(rules, move.param)) return { ok: false, error: 'param_out_of_domain' };

  const landing = findLanding(rules, state.board, move.param);
  if (landing === -1) return { ok: false, error: 'no_landing_cell' };

  const next = state.board.slice();
  next[landing] = seat;
  const board = Object.freeze(next);

  const line = winLineThrough(rules, board, landing, seat);
  const status: Status<S> =
    line === null
      ? isDrawn(rules, board)
        ? DRAW
        : IN_PLAY
      : Object.freeze({
          kind: 'win' as const,
          winner: seat,
          // Length guaranteed by construction: `winLineThrough` slices exactly
          // `condition.length` indices, which is `WinLengthOf<S>`.
          line: line as unknown as Line<WinLengthOf<S>>,
        });

  return {
    ok: true,
    primary: landing,
    written: Object.freeze([landing]),
    state: Object.freeze({
      board,
      toMove: nextSeat(rules, seat),
      moveCount: state.moveCount + 1,
      status,
    }),
  };
}

/**
 * Rebuild a game from its move log. Seats rotate strictly from `firstSeat`, so
 * the log never needs to record who moved.
 */
export function replay<S extends RulesSpec>(
  rules: S,
  firstSeat: SeatOf<S>,
  moves: readonly Move[],
): ReplayResult<S> {
  let state = createInitial(rules, firstSeat);
  for (const [index, move] of moves.entries()) {
    const result = applyMove(rules, state, state.toMove, move);
    if (!result.ok) return { ok: false, atIndex: index, error: result.error };
    state = result.state;
  }
  return { ok: true, state };
}

/** `encoding: 'digits'` — one char per cell in index order. Stable across versions. */
export function encodeBoard<S extends RulesSpec>(rules: S, board: BoardOf<S>): string {
  const size = boardSize(rules);
  let out = '';
  for (let index = 0; index < size; index += 1) {
    out += String(board[index] ?? rules.cells.empty);
  }
  return out;
}

/**
 * Inverse of `encodeBoard`. The one sanctioned throw in the engine: malformed
 * text is a programming error at the storage boundary, not a game outcome.
 */
export function decodeBoard<S extends RulesSpec>(rules: S, encoded: string): BoardOf<S> {
  const size = boardSize(rules);
  if (encoded.length !== size) {
    throw new RangeError(`encoded board must be ${size} chars, got ${encoded.length}`);
  }
  const empty = String(rules.cells.empty);
  const board = new Array<CellOf<S>>(size);
  for (let index = 0; index < size; index += 1) {
    const char = encoded[index];
    if (char === empty) {
      board[index] = rules.cells.empty;
      continue;
    }
    const seat = rules.seats.find((candidate) => String(candidate) === char);
    if (seat === undefined) {
      throw new RangeError(`invalid cell '${String(char)}' at index ${index}`);
    }
    board[index] = seat;
  }
  return Object.freeze(board);
}
